import React, { useState, useEffect } from "react";
import { CreditCard, Globe, Smartphone, AlertCircle, Laptop, User, Building2, Edit3, Save, X } from "lucide-react";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/card";
import {Badge} from "../../../components/ui/badge";
import {Switch} from "../../../components/ui/switch";
import { Input } from "../../../components/ui/input";
import Label from "../../../components/ui/label";
import StripeConnectSetup from "../../../components/ui/StripeConnectSetup";

import { useDispatch, useSelector } from "react-redux";
import { selectedProfileSetting } from "../../../redux";
import { UpdateUserProfile } from "../../../redux/Actions/AuthAction";
import { GlobalService } from "../../../modules";
import NotificationComponent from "../../client/components/NotificationComponent";
import PrivacyComponent from "../../client/components/PrivacyComponent";
import ProfileSidebarComponent from "./ProfileSidebarComponent";
import SharedHeader4 from "../../../Headers/SharedHeader4";
import GeneralSetting from "./GeneralSetting";
import SharedFooter3 from "../../../Footer/SharedFooter3";
import CompanyProfileSetting from "../../client/components/CompanyProfileSetting";
import BillingComponent from "../../client/components/BillingComponent";
import { useTranslation } from "react-i18next";

const MySettings = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { user } = useSelector((state: any) => state.auth);
    
    // State for various settings
    const [settings, setSettings] = useState({
        notifications: {
            email: {
                newMessages: true,
                projectInvites: true,
                projectUpdates: true,
                marketing: false,
            },
            push: {
                newMessages: true,
                projectInvites: true,
                projectUpdates: false,
                marketing: false,
            },
            desktop: {
                enabled: true,
                soundEnabled: true,
            },
        },
        privacy: {
            profileVisibility: "public",
            showEarnings: true,
            showLastSeen: true,
            showAvailability: true,
        },
        communication: {
            preferredLanguage: "English",
            timezone: "America/New_York",
            currency: "USD",
        },
        security: {
            twoFactorEnabled: true,
            loginNotifications: true,
            devices: [
                {
                    id: 1,
                    name: "MacBook Pro",
                    lastActive: "2 hours ago",
                    location: "Toronto, Canada",
                    current: true,
                },
                {
                    id: 2,
                    name: "iPhone 13",
                    lastActive: "5 mins ago",
                    location: "Toronto, Canada",
                    current: true,
                },
            ],
        },
        billing: {
            defaultPayment: {
                type: "credit_card",
                last4: "4242",
                expiryDate: "12/24",
            },
            billingAddress: {
                street: "123 Main St",
                city: "Toronto",
                state: "ON",
                zip: "M5V 2T6",
                country: "Canada",
            },
            taxId: "TAX123456",
            autoRecharge: true,
            rechargeThreshold: 100,
            rechargeAmount: 500,
        },
    });

    const activeSection = useSelector(selectedProfileSetting);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
            <SharedHeader4 />
            <div className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
                <div className="flex flex-col md:flex-row gap-6">
                    <ProfileSidebarComponent />
                    {/* Settings Content */}
                    <div className="flex-1 space-y-6">
                        {activeSection === "general" && <GeneralSetting />}

                        {activeSection === "notifications" && <NotificationComponent />}

                        {activeSection === "privacy" && <PrivacyComponent />}
                        {activeSection === "company" && <CompanyProfileSetting />}
                        {activeSection === "security" && (
                            <>
                                {/* Security Settings */}
                                <Card color={undefined} className="p-6">
                                    <h2 className="text-xl font-semibold mb-6">{t("mySettings.securitySettings")}</h2>
                                    <div className="space-y-6">
                                        {/* Two-Factor Authentication */}
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="font-medium">{t("mySettings.twoFactorAuth")}</h3>
                                                <p className="text-sm text-gray-500">
                                                    {t("mySettings.twoFactorAuthDesc")}
                                                </p>
                                            </div>
                                            <Switch
                                                checked={settings.security.twoFactorEnabled}
                                                onCheckedChange={() =>
                                                    setSettings(prev => ({
                                                        ...prev,
                                                        security: {
                                                            ...prev.security,
                                                            twoFactorEnabled: !prev.security.twoFactorEnabled,
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>

                                        {/* Login Notifications */}
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="font-medium">{t("mySettings.loginNotifications")}</h3>
                                                <p className="text-sm text-gray-500">
                                                    {t("mySettings.loginNotificationsDesc")}
                                                </p>
                                            </div>
                                            <Switch
                                                checked={settings.security.loginNotifications}
                                                onCheckedChange={() =>
                                                    setSettings(prev => ({
                                                        ...prev,
                                                        security: {
                                                            ...prev.security,
                                                            loginNotifications: !prev.security.loginNotifications,
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>

                                        {/* Active Sessions */}
                                        <div className="border-t pt-6">
                                            <h3 className="font-medium mb-4">{t("mySettings.activeSessions")}</h3>
                                            <div className="space-y-4">
                                                {settings.security.devices.map(device => (
                                                    <div
                                                        key={device.id}
                                                        className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                                                        <div className="flex items-center gap-4">
                                                            {device.name.includes("iPhone") ? (
                                                                <Smartphone className="h-5 w-5 text-gray-500" />
                                                            ) : (
                                                                <Laptop className="h-5 w-5 text-gray-500" />
                                                            )}
                                                            <div>
                                                                <div className="font-medium">{device.name}</div>
                                                                <div className="text-sm text-gray-500">
                                                                    {device.location} • {device.lastActive}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {!device.current && (
                                                            <Button variant="outline" size="sm">
                                                                {t("mySettings.revokeAccess")}
                                                            </Button>
                                                        )}
                                                        {device.current && (
                                                            <Badge
                                                                variant={undefined}
                                                                className="bg-green-50 text-green-700">
                                                                {t("mySettings.currentDevice")}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </>
                        )}

                        {activeSection === "billing" && <BillingComponent />}

                        {activeSection === "connected" && (
                            <>
                                {/* Connected Accounts */}
                                <Card color={undefined} className="p-6">
                                    <h2 className="text-xl font-semibold mb-6">{t("mySettings.connectedAccounts")}</h2>
                                    <div className="space-y-4">
                                        {/* GitHub */}
                                        {/* <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Github className="h-6 w-6" />
                        <div>
                          <h3 className="font-medium">GitHub</h3>
                          <p className="text-sm text-gray-500">
                            Connect your GitHub account
                          </p>
                        </div>
                      </div>
                      <Button variant="outline">Connect</Button>
                    </div> */}

                                        {/* LinkedIn */}
                                        {/* <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-4">
                        <Linkedin className="h-6 w-6" />
                        <div>
                          <h3 className="font-medium">LinkedIn</h3>
                          <p className="text-sm text-gray-500">
                            Connect your LinkedIn account
                          </p>
                        </div>
                      </div>
                      <Button variant="outline">Connect</Button>
                    </div> */}

                                        {/* StackOverflow */}
                                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                            <div className="flex items-center gap-4">
                                                <Globe className="h-6 w-6" />
                                                <div>
                                                    <h3 className="font-medium">{t("mySettings.stackOverflow")}</h3>
                                                    <p className="text-sm text-gray-500">
                                                        {t("mySettings.connectStackOverflow")}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button variant="outline">{t("mySettings.connect")}</Button>
                                        </div>
                                    </div>
                                </Card>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Account Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <Card color={undefined} className="w-full max-w-md mx-4 p-6">
                        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-red-600">
                            <AlertCircle className="h-5 w-5" />
                            {t("mySettings.deleteAccountTitle")}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {t("mySettings.deleteAccountWarning")}
                        </p>
                        <div className="flex justify-end gap-4">
                            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                                {t("mySettings.cancel")}
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={() => {
                                    // Handle account deletion
                                    setShowDeleteConfirm(false);
                                }}>
                                {t("mySettings.deleteAccountButton")}
                            </Button>
                        </div>
                    </Card>
                </div>
            )}

            {/* Footer */}
            <footer className="bg-white border-t w-full mt-auto">
                <div className="max-w-7xl mx-auto">
                    <SharedFooter3 />
                </div>
            </footer>
        </div>
    );
};

export default MySettings;
